import { useFrame, useLoader } from '@react-three/fiber';
import { TextureLoader } from 'three';
import { useMemo, useRef } from 'react';

export default function SpaceBackground({ url }) {
  const matRef = useRef();
  const tex = useLoader(TextureLoader, url);

  // prevent ugly stretch on non-square screens
  tex.wrapS = tex.wrapT = 1000; // RepeatWrapping without importing
  tex.repeat.set(1, 1);

  const uniforms = useMemo(
    () => ({
      time: { value: 0 },
      map: { value: tex },
      strength: { value: 0.065 }, // swirl amount (try 0.03–0.10)
      speed: { value: 0.08 }, // motion speed
    }),
    [tex]
  );

  useFrame((state) => {
    if (matRef.current)
      matRef.current.uniforms.time.value = state.clock.elapsedTime;
  });

  return (
    <mesh>
      <planeGeometry args={[2, 2, 1, 1]} />
      <shaderMaterial
        ref={matRef}
        uniforms={uniforms}
        depthWrite={false}
        vertexShader={`
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = vec4(position, 1.0);
          }
        `}
        fragmentShader={`
          uniform float time;
          uniform sampler2D map;
          uniform float strength;
          uniform float speed;
          varying vec2 vUv;

          // swirl around center + subtle “warping” drift
          void main() {
            vec2 uv = vUv;

            // center-space coords
            vec2 p = uv - 0.5;
            float r = length(p);

            // time
            float t = time * speed;

            // swirl angle: stronger toward edges, animated slightly
            float a = strength * (r * r) * 8.0;
            a += 0.02 * sin(t * 2.0 + r * 10.0);

            float s = sin(a), c = cos(a);
            p = mat2(c, -s, s, c) * p;

            // subtle drift so the whole field feels alive
            p += vec2(0.01 * sin(t + p.y * 6.0), 0.01 * cos(t * 1.2 + p.x * 6.0));

            uv = p + 0.5;

            // very slight radial zoom pulsing (makes it feel “spacey”)
            uv = (uv - 0.5) * (1.0 + 0.01 * sin(t)) + 0.5;

            vec4 col = texture2D(map, uv);

            // optional: tiny darkening vignette so center reads better
            float vign = smoothstep(0.95, 0.15, r);
            col.rgb *= mix(0.85, 1.0, vign);

            gl_FragColor = col;
          }
        `}
      />
    </mesh>
  );
}
